import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, IndianRupee, Wallet } from 'lucide-react';
import { PAYABLE_STAGES, type PayableStage } from '@tailoring-crm/shared';
import { RateMatrix } from '../components/staff/rate-matrix.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { useConfirm, Badge, Button, PageSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import {
  useSetStaffActive,
  useSetStaffRate,
  useStaffMember,
  useStaffRates,
  useUpdateStaffMember,
} from '../lib/staff/use-staff.js';
import { useCraftRoles } from '../lib/staff/use-craft-roles.js';
import { offerableCraftRoles } from '../lib/staff/craft-roles-api.js';
import { formatDate, formatMobile } from '../lib/format.js';

/**
 * One person: who they are, what they handle, and what they are paid.
 *
 * The two empty sections at the bottom are deliberate rather than unfinished. Task 20 fills in
 * the work they are assigned and task 21 their earnings, and both belong on this screen — so
 * the shape is here now, saying what is coming, instead of being retrofitted into a layout that
 * never expected them.
 *
 * Everything about money on this screen is behind the same rule the API enforces: a tailor
 * opening their own page sees their rate grid read-only, and the buttons that would change it
 * are not rendered for them — because a button in front of an endpoint that refuses is a bug,
 * not a cosmetic detail.
 */
export function StaffDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { staffId = '' } = useParams<{ staffId: string }>();
  const { role, user } = useAuth();
  const confirm = useConfirm();

  const canManage = role === 'owner' || role === 'manager';
  const isSelf = user?.id === staffId;

  const member = useStaffMember(staffId);
  const craftRoles = useCraftRoles();
  const rates = useStaffRates(staffId);
  const setRate = useSetStaffRate(staffId);
  const update = useUpdateStaffMember(staffId);
  const setActive = useSetStaffActive();

  /**
   * The shift fields, as drafts.
   *
   * `null` means "nobody has typed in this box yet", which is not the same as an empty shift —
   * so the inputs fall back to what is stored, and to the shop's own opening hours for somebody
   * who has no shift at all. Declared here, above the loading and not-found returns, because a
   * hook may not sit after one.
   */
  const [startDraft, setStartDraft] = useState<string | null>(null);
  const [endDraft, setEndDraft] = useState<string | null>(null);

  if (member.isPending) return <PageSkeleton label={t('common.loading')} />;
  if (!member.data) {
    return (
      <EmptyState
        icon={ClipboardList}
        title={t('staff.notFound')}
        description={t('staff.notFoundHint')}
      />
    );
  }

  const person = member.data;
  // A sensible pair to start from when the shop has never given this person their own hours.
  const shiftStart = startDraft ?? person.shift?.start ?? '10:00';
  const shiftEnd = endDraft ?? person.shift?.end ?? '20:00';
  const canEditRates = canManage;
  const canDeactivate = role === 'owner' && person.role !== 'owner' && !isSelf;

  /** Deactivating is not destructive — nothing is deleted — but it does lock somebody out. */
  const askThenDeactivate = async (): Promise<void> => {
    const ok = await confirm({
      title: t('staff.deactivateTitle', { name: person.name }),
      description: t('staff.deactivateWarning'),
      confirmLabel: t('staff.deactivate'),
    });
    if (!ok) return;
    setActive.mutate(
      { userId: staffId, active: false },
      {
        onSuccess: () => toast({ title: t('staff.deactivated', { name: person.name }) }),
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  /**
   * Saving a trade label through the ordinary update-staff mutation.
   *
   * It goes through the same `PATCH /staff/:userId` that specialisations do, under the same
   * permission, because it grants nothing: nobody's access changes when their trade does.
   */
  const setCraftRole = (next: string | null): void => {
    update.mutate(
      { craftRole: next },
      {
        onSuccess: () => toast({ title: t('staff.craftRole.saved') }),
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  /**
   * Their own hours. `null` puts them back on the shop's workday.
   *
   * Through the same `PATCH /staff/:userId` the trade label uses, under the same permission,
   * for the same reason: it grants nothing. What it does change is whether an arrival reads as
   * late, which is why the server audits it.
   */
  const saveShift = (next: { start: string; end: string } | null): void => {
    update.mutate(
      { shift: next },
      {
        onSuccess: () => toast({ title: t('staff.shift.saved') }),
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  const toggleSpecialisation = (stage: PayableStage): void => {
    const next = person.specialisations.includes(stage)
      ? person.specialisations.filter((one) => one !== stage)
      : [...person.specialisations, stage];
    update.mutate(
      { specialisations: next },
      {
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  return (
    <>
      <div className="pb-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/staff">
            <ArrowLeft aria-hidden="true" />
            {t('staff.backToList')}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={person.name}
        description={
          person.phone ? `${person.email} · ${formatMobile(person.phone)}` : person.email
        }
        action={
          canDeactivate ? (
            person.active ? (
              <Button variant="outline" onClick={() => void askThenDeactivate()}>
                {t('staff.deactivate')}
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={setActive.isPending}
                onClick={() =>
                  setActive.mutate(
                    { userId: staffId, active: true },
                    { onSuccess: () => toast({ title: t('staff.reactivated') }) },
                  )
                }
              >
                {t('staff.reactivate')}
              </Button>
            )
          ) : undefined
        }
      />

      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={person.role === 'owner' ? 'default' : 'secondary'}>
            {t(`role.${person.role}`)}
          </Badge>
          {person.active ? null : <Badge variant="warning">{t('staff.inactive')}</Badge>}
          <span className="text-sm text-muted-foreground">
            {t('staff.joinedOn', { date: formatDate(person.joinedAt) })}
          </span>
        </div>

        {/* What they are called on the floor. Separate from the role badge above and from the
            stages below, because it is a third and different fact: the badge says what they may
            do, the stages say what work suits them, and this says what their trade is. */}
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('staff.craftRole.label')}</h3>
          {canManage ? (
            <>
              <select
                id="staff-craft-role"
                aria-label={t('staff.craftRole.label')}
                value={person.craftRole ?? ''}
                disabled={update.isPending}
                onChange={(event) => setCraftRole(event.target.value || null)}
                className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
              >
                <option value="">{t('staff.craftRole.noneOption')}</option>
                {offerableCraftRoles(craftRoles.data?.roles).map((craftRole) => (
                  <option key={craftRole.key} value={craftRole.key}>
                    {craftRole.label}
                  </option>
                ))}
                {/* A trade the shop has since retired still has to be selectable-as-current, or
                    opening the page would silently show this person as having none. */}
                {person.craftRole !== null &&
                !offerableCraftRoles(craftRoles.data?.roles).some(
                  (craftRole) => craftRole.key === person.craftRole,
                ) &&
                person.craftRoleLabel !== null ? (
                  <option value={person.craftRole}>{person.craftRoleLabel}</option>
                ) : null}
              </select>
              <p className="text-sm text-muted-foreground">{t('staff.craftRole.hint')}</p>
            </>
          ) : person.craftRoleLabel ? (
            <p className="text-sm">{person.craftRoleLabel}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('staff.craftRole.empty')}</p>
          )}
        </section>

        {/*
          Their own working hours, when they differ from the shop's.

          It sits here rather than in attendance settings because it is a fact about a PERSON,
          not about the shop — and because the one thing it changes is whether this person's
          arrival counts as late, which is a conversation about them. Almost every shop leaves
          it empty and everybody is measured against the shop's own workday.
        */}
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('staff.shift.label')}</h3>
          {canManage ? (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  {t('staff.shift.start')}
                  <input
                    type="time"
                    aria-label={t('staff.shift.start')}
                    value={shiftStart}
                    disabled={update.isPending}
                    onChange={(event) => setStartDraft(event.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  {t('staff.shift.end')}
                  <input
                    type="time"
                    aria-label={t('staff.shift.end')}
                    value={shiftEnd}
                    disabled={update.isPending}
                    onChange={(event) => setEndDraft(event.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  />
                </label>
                <Button
                  size="sm"
                  disabled={update.isPending || shiftStart === '' || shiftEnd === ''}
                  onClick={() => saveShift({ start: shiftStart, end: shiftEnd })}
                >
                  {t('staff.shift.save')}
                </Button>
                {/* Clearing it puts them back on the shop's hours, which is where almost
                    everybody belongs — so it is one button, not a checkbox somebody has to
                    understand first. */}
                {person.shift ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={update.isPending}
                    onClick={() => saveShift(null)}
                  >
                    {t('staff.shift.clear')}
                  </Button>
                ) : null}
              </div>
              <p className="text-sm text-muted-foreground">
                {person.shift ? t('staff.shift.hint') : t('staff.shift.shopHoursHint')}
              </p>
            </>
          ) : person.shift ? (
            <p className="text-sm">
              {person.shift.start} – {person.shift.end}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('staff.shift.shopHoursHint')}</p>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('staff.field.specialisations')}</h3>
          {canManage ? (
            <>
              <div className="flex flex-wrap gap-2">
                {PAYABLE_STAGES.map((stage) => {
                  const active = person.specialisations.includes(stage);
                  return (
                    <button
                      key={stage}
                      type="button"
                      aria-pressed={active}
                      disabled={update.isPending}
                      onClick={() => toggleSpecialisation(stage)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
                        active
                          ? 'border-transparent bg-primary text-primary-foreground'
                          : 'border-border hover:bg-accent',
                      )}
                    >
                      {t(`stage.${stage}`)}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground">{t('staff.specialisationsHint')}</p>
            </>
          ) : person.specialisations.length > 0 ? (
            <p className="text-sm">
              {person.specialisations.map((stage) => t(`stage.${stage}`)).join(', ')}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('staff.noSpecialisations')}</p>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">{t('staff.rates.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('staff.rates.description')}</p>
          </div>

          {rates.isPending ? (
            <PageSkeleton label={t('common.loading')} />
          ) : rates.data && rates.data.matrix.length > 0 ? (
            <RateMatrix
              matrix={rates.data.matrix}
              stages={PAYABLE_STAGES}
              pending={setRate.isPending}
              {...(canEditRates
                ? {
                    onSetRate: ({ garmentTypeKey, stage, ratePaise }) =>
                      setRate.mutate(
                        { garmentTypeKey, stage, ratePaise },
                        {
                          onSuccess: () => toast({ title: t('staff.rates.saved') }),
                          onError: () =>
                            toast({ title: t('error.unexpected'), variant: 'destructive' }),
                        },
                      ),
                    // Clearing writes a dated tombstone rather than deleting anything: the
                    // person returns to the shop rate from today, and what they were paid
                    // before today is untouched.
                    onClearRate: ({ garmentTypeKey, stage }) =>
                      setRate.mutate(
                        { garmentTypeKey, stage, ratePaise: null },
                        { onSuccess: () => toast({ title: t('staff.rates.cleared') }) },
                      ),
                  }
                : {})}
            />
          ) : (
            <EmptyState
              icon={IndianRupee}
              title={t('staff.rates.emptyTitle')}
              description={t('staff.rates.emptyHint')}
            />
          )}
        </section>

        {/* Where task 20 still lands. Earnings are a screen of their own now (task 21): what
            somebody is owed is a working document with jobs to tick, not a panel on a profile. */}
        <section className="grid gap-3 sm:grid-cols-2">
          <EmptyState
            icon={ClipboardList}
            title={t('staff.workTitle')}
            description={t('staff.workComingSoon')}
          />
          <EmptyState
            icon={Wallet}
            title={t('staff.earningsTitle')}
            description={t('staff.earningsHint')}
            action={
              canManage ? (
                <Button asChild>
                  <Link to={`/payouts/staff/${staffId}`}>{t('staff.openEarnings')}</Link>
                </Button>
              ) : undefined
            }
          />
        </section>
      </div>
    </>
  );
}

export default StaffDetailPage;
